import React, { Fragment, useEffect, useRef, useState } from "react";

// External Components
import Popover from "@material-ui/core/Popover";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

// External Styles
import "react-toastify/dist/ReactToastify.css";

// Local Stylesheet
import "./AdhocUploadDocuments.scss";

// Local Assets
import calender from "../../assets/icons/calender-icon.svg";
import fileIcon from "../../assets/icons/file-icon.svg";
import tickCheck from "../../assets/icons/tickCheck.svg";

// Apollo Client
import { gql, useMutation, useQuery } from "@apollo/client";

// External Libraries
import { uploadClient } from "../..";

// Custom Components and Utilities
import StatusChip from "../../components/Request/RightSection/Table/StatusChip/StatusChip";
import { notify } from "../../components/Notify/Notify";
import Spinner from "react-bootstrap/Spinner";
import classNames from "classnames";
import { downloadFile, formatISODateToLongDate } from "../../helper";
import { logoutGuestUser } from "../../auth";
import { sort } from "fast-sort";
import { isDueDatePast } from "../../components/RequestDetailedView/RequestDetailedView";
import { jwtDecode } from "jwt-decode";

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  fabIcon: {
    minHeight: "48px !important",
    minWidth: "120px !important",
    borderRadius: "24px !important",
    backgroundColor: "#2563eb !important",
  },
  saveIcon: {
    marginRight: theme.spacing(1),
  },
  typography: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(4),
    cursor: "pointer",
  },
}));

export const UPLOAD_DOCUMENT_REQUEST_SUBMISSIONS = gql`
  mutation (
    $documentSubmissionsInfo: [DocumentRequestSubmissionInputType!]!
    $files: [Upload!]!
  ) {
    uploadDocumentRequestSubmissions(
      documentSubmissionsInfo: $documentSubmissionsInfo
      files: $files
    ) {
      success
    }
  }
`;

export const GET_PRESIGNED_URL = gql`
  mutation GetPresignedUrlForSubmission($documentRequestSubmissionId: String!) {
    data: downloadDocumentRequestSubmission(
      documentRequestSubmissionId: $documentRequestSubmissionId
    ) {
      success
      url
    }
  }
`;
const GET_REQUEST_DOCUMENT = gql`
  mutation GetRequestDocument($id: String!) {
    data: getRequestDocument(documentRequestDocumentId: $id) {
      success
      url
    }
  }
`;

// Queries
export const DOCUMENT_REQUEST_USER_SUBMISSIONS = gql`
  query DocumentRequestUserSubmissions {
    data: documentrequestUserSubmissions {
      id
      submissionStatus
      submissionFile
      submittedAt
      documentRequestDocument {
        id
        documentName
        document
      }
      documentRequestUser {
        id
        submissionStatus
        email
        documentRequest {
          id
          title
          dueDate
          documentsCount
          status
          emailDescription
        }
      }
    }
  }
`;

const GET_ORGANIZATION = gql`
  query {
    data: getOrganization {
      name
    }
  }
`;

function AdhocUploadDocuments() {
  const {
    data: { data: requestData = {} } = {},
    loading: isRequestDataLoading,
    error: requestDatError,
  } = useQuery(DOCUMENT_REQUEST_USER_SUBMISSIONS, {
    client: uploadClient,
    fetchPolicy: "network-only",
  });

  const { data: organizationData, loading: isLoadingOrganization } = useQuery(
    GET_ORGANIZATION,
    {
      client: uploadClient,
      fetchPolicy: "network-only",
    }
  );
  const [requestIdComingFromUrlParams, setRequestIdComingFromUrlParams] =
    useState(null);
  const { userDetails, requestDetails, documents } =
    transformRequestData(requestData);

  const [uploadedFiles, setUploadedFiles] = useState({});

  const handleFileUpload = (documentId, selectedFile) => {
    // Create a copy of the uploadedFiles state and update it with the new file
    const updatedFiles = { ...uploadedFiles };
    updatedFiles[documentId] = { file: selectedFile, name: selectedFile.name };
    setUploadedFiles(updatedFiles);
  };

  // Mutations
  const [uploadDocuments, { loading: isUploadDocumentsLoading }] = useMutation(
    UPLOAD_DOCUMENT_REQUEST_SUBMISSIONS,
    {
      client: uploadClient,
      refetchQueries: [{ query: DOCUMENT_REQUEST_USER_SUBMISSIONS }],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Cancel Request Mutation Error:", error);
      },
      onCompleted: () => {
        setUploadedFiles({});
        notify("Success", "File(s) are successfully uploaded", "success");
      },
      awaitRefetchQueries: true,
    }
  );
  const handleSaveChanges = () => {
    // Perform actions to save changes
    const { files, documentSubmissionsInfo } =
      createStateVariables(uploadedFiles);
    uploadDocuments({
      variables: {
        documentSubmissionsInfo: documentSubmissionsInfo,
        files: files,
      },
    });
  };
  const isSaveChangesBtnDisabled =
    isUploadDocumentsLoading || Object.keys(uploadedFiles).length === 0;

  // Logout Btn related
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  useEffect(() => {
    if (requestDatError) {
      logoutGuestUser();
      window.location.reload();
    }
  }, [requestDatError]);
  const isRequestExpired =
    requestDetails?.status?.toLowerCase() === "pending" &&
    isDueDatePast(requestDetails?.dueDate, requestDetails?.status);

  const isRequestPending =
    requestDetails?.status?.toLowerCase() === "pending" &&
    !isDueDatePast(requestDetails?.dueDate, requestDetails?.status) &&
    requestDetails?.userOwnRequestStatus?.toLowerCase() === "pending";

  const isCancelledRequest =
    requestDetails?.status?.toLowerCase() === "cancelled" ||
    requestDetails?.userOwnRequestStatus?.toLowerCase() === "cancelled";

  const isAllDocsSubmitted = documents.every(
    (doc) => doc?.status?.toUpperCase() === "SUBMITTED"
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("u");
    if (!code) {
      return;
    }
    const decoded = jwtDecode(code);
    if (decoded?.document_request_id) {
      setRequestIdComingFromUrlParams(String(decoded?.document_request_id));
    }
    // You can now use the value of `code` as needed
  }, []);
  const requestId = requestDetails?.id;
  useEffect(() => {
    if (
      typeof requestIdComingFromUrlParams === "string" &&
      typeof requestId === "string" &&
      !isRequestDataLoading &&
      requestIdComingFromUrlParams !== requestId
    ) {
      logoutGuestUser();
      handleClose();
      window.location.reload();
    }
  }, [requestIdComingFromUrlParams, isRequestDataLoading, requestId]);
  const watermarkStyle = {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "14px", // Adjust as needed
    color: "#888", // Adjust as needed
    paddingBottom: "12px",
    paddingTop: "20px",
  };
  return (
    <Fragment>
      <div
        className="adhoc-upload-documents-container new-design-v1"
        style={{}}
      >
        <div className="adhoc-upload-documents-container-header">
          <div className="adhoc-upload-documents-container-header-label">
            {isLoadingOrganization
              ? "Loading...."
              : organizationData?.data?.name ?? "Tikler Document Workflows"}
          </div>
          <div
            className="adhoc-upload-documents-container-header-email-label cursor-pointer"
            aria-describedby={id}
            onClick={handleClick}
          >
            {userDetails?.email ?? "Logout"}
          </div>
        </div>
        <div className="adhoc-upload-documents-container-sub-header">
          <div className="adhoc-upload-documents-container-sub-header-label">
            {requestDetails?.title ?? "  Upload Documents"}
          </div>
          {!isRequestDataLoading && (
            <div className="adhoc-upload-documents-container-sub-header-more-info gap-8">
              <div className="adhoc-upload-documents-container-sub-header-more-info-wrapper">
                <div style={{ maxWidth: "96px" }}>
                  <StatusChip
                    value={getStatusValue(
                      isRequestExpired,
                      isRequestPending,
                      isAllDocsSubmitted,
                      isCancelledRequest
                    )}
                  />
                </div>
                <div className="d-flex align-items-center ">
                  <img src={fileIcon} alt="fileIcon" />
                  <div className="pl-2">{`${
                    requestDetails?.documentsCount ?? ""
                  } Documents Requested`}</div>
                </div>
                <div className="d-flex align-items-center ">
                  <img src={calender} alt="calender" />
                  <div className="pl-2">{`Due on ${formatISODateToLongDate(
                    requestDetails?.dueDate
                  )}`}</div>
                </div>
              </div>
              <div>
                <button
                  className="primary-button"
                  onClick={handleSaveChanges}
                  disabled={isSaveChangesBtnDisabled || !isRequestPending}
                  style={{ minWidth: "160px", maxWidth: "160px" }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
          {isRequestDataLoading && (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="border" role="status" />
            </div>
          )}
        </div>
        <div className="adhoc-upload-documents-container-form">
          <div className="adhoc-upload-documents-container-form-header">
            Description
          </div>
          <div className="mt-6 adhoc-upload-documents-container-form-description">
            <div>{requestDetails?.emailDescription}</div>

            {Object.keys(uploadedFiles).length > 0 && (
              <div className="mt-3 text-center font-weight-bold font-italic">
                {" "}
                {`Your ${
                  Object.keys(uploadedFiles).length === 1
                    ? "file is"
                    : "files are"
                }  ready to be uploaded. Click 'Save Changes' to complete the upload process.`}{" "}
              </div>
            )}
            {(isCancelledRequest || isRequestExpired) &&
              !isRequestDataLoading && (
                <div
                  className="mt-3 text-center font-weight-bold font-italic"
                  style={{ color: "#dc3545" }}
                >
                  {
                    "This request is currently not accepting submissions. If you have any questions or need further assistance, kindly contact the requester directly. We appreciate your understanding"
                  }
                </div>
              )}
          </div>
          <DocumentList
            documents={documents}
            onFileUpload={handleFileUpload}
            uploadedFiles={uploadedFiles}
            isLoading={isRequestDataLoading}
            isRequestPending={isRequestPending}
          />
        </div>
        <div className="px-8 py-4 d-block d-lg-none">
          <button
            className="primary-button"
            onClick={handleSaveChanges}
            disabled={isSaveChangesBtnDisabled || !isRequestPending}
          >
            Save Changes
          </button>
        </div>
        <div style={watermarkStyle}>
          <span style={{ paddingTop: "4px", fontSize: "20px" }}>
            {" "}
            Powered by{" "}
          </span>
          <img
            src="https://test.tikler.io/static/media/tickler_logo.cb26ceaf.svg"
            alt="Tikler Logo"
            style={{ height: "36px", width: "98px", marginLeft: "5px" }}
          />
        </div>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Typography
          className={classes.typography}
          onClick={() => {
            logoutGuestUser();
            handleClose();
            window.location.reload();
          }}
        >
          Logout
        </Typography>
      </Popover>
    </Fragment>
  );
}

export default AdhocUploadDocuments;

// Helper component for rendering document list
function DocumentList({
  documents,
  onFileUpload,
  uploadedFiles,
  isLoading = false,
  isRequestPending = true,
}) {
  return (
    <div className="adhoc-upload-documents-container-form-inputs-table mt-8">
      <div className="adhoc-upload-documents-container-form-inputs-table-header">
        {"Requested Documents"}
      </div>
      {isLoading && (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status" />
        </div>
      )}
      {!isLoading &&
        documents.map((document, index) => (
          <DocumentCard
            key={document.id}
            document={document}
            onFileUpload={onFileUpload}
            uploadedFiles={uploadedFiles}
            shouldThickTheBorder={index !== documents.length - 1}
            isRequestPending={isRequestPending}
          />
        ))}
    </div>
  );
}

// Helper component for rendering document card
function DocumentCard({
  document,
  onFileUpload,
  uploadedFiles,
  shouldThickTheBorder = true,
  isRequestPending = true,
}) {
  const [
    getPresignedUrlForSubmission,
    { loading: isGetPresignedUrlForSubmissionLoading },
  ] = useMutation(GET_PRESIGNED_URL);
  const [
    getPresignedUrlForTemplateDocs,
    { loading: isGetPresignedUrlForTemplateDocsLoading },
  ] = useMutation(GET_REQUEST_DOCUMENT);

  const renderActions = () => {
    switch (document.status?.toLowerCase()) {
      case "submitted":
        return (
          <>
            <div className="action-btn cursor-pointer">
              <UploadFileLink
                label="Re-Upload File"
                documentId={document.id}
                onFileUpload={onFileUpload}
                isRequestPending={isRequestPending}
              />
            </div>
            <div className="d-flex w-100 common-border-bottom dummy-div"></div>
            <div
              className="action-btn cursor-pointer "
              style={{
                opacity: isGetPresignedUrlForSubmissionLoading ? 0.5 : 1,
              }}
              onClick={async () => {
                if (isGetPresignedUrlForSubmissionLoading) {
                  return;
                }
                try {
                  const data = await getPresignedUrlForSubmission({
                    variables: { documentRequestSubmissionId: document.id },
                  });
                  const url = data?.data?.data?.url;
                  if (url) {
                    downloadFile(url, `${document.documentName}.pdf`);
                  }
                } catch (error) {
                  notify("Error", "Something went wrong.", "error");
                }
              }}
            >
              Download Submission
            </div>
            <div className="d-flex w-100 common-border-bottom dummy-div"></div>
            <div
              className="action-btn cursor-pointer"
              style={{
                opacity:
                  isGetPresignedUrlForTemplateDocsLoading ||
                  document?.requestedDocument === -1
                    ? 0.5
                    : 1,
                cursor:
                  isGetPresignedUrlForTemplateDocsLoading ||
                  document?.requestedDocument === -1
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={async () => {
                if (document?.requestedDocument === -1) {
                  return;
                }
                try {
                  if (isGetPresignedUrlForTemplateDocsLoading) {
                    return;
                  }
                  const data = await getPresignedUrlForTemplateDocs({
                    variables: {
                      id: document.documentId,
                    },
                  });
                  const url = data?.data?.data?.url;
                  if (url) {
                    downloadFile(url, `${document.documentName}.pdf`);
                  }
                } catch (error) {
                  notify("Error", "Something went wrong.", "error");
                }
              }}
            >
              Download File
            </div>
          </>
        );
      case "pending":
        return (
          <>
            <div className="action-btn cursor-pointer">
              <UploadFileLink
                label="Upload File"
                documentId={document.id}
                onFileUpload={onFileUpload}
                isRequestPending={isRequestPending}
              />
            </div>
            <div className="d-flex w-100 common-border-bottom dummy-div"></div>
            <div
              className="action-btn cursor-pointer"
              style={{
                opacity:
                  isGetPresignedUrlForTemplateDocsLoading ||
                  document?.requestedDocument === -1
                    ? 0.5
                    : 1,
                cursor:
                  isGetPresignedUrlForTemplateDocsLoading ||
                  document?.requestedDocument === -1
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={async () => {
                if (document?.requestedDocument === -1) {
                  return;
                }
                try {
                  if (isGetPresignedUrlForTemplateDocsLoading) {
                    return;
                  }
                  const data = await getPresignedUrlForTemplateDocs({
                    variables: {
                      id: document.documentId,
                    },
                  });
                  const url = data?.data?.data?.url;
                  if (url) {
                    downloadFile(url, `${document.documentName}.pdf`);
                  }
                } catch (error) {
                  notify("Error", "Something went wrong.", "error");
                }
              }}
            >
              Download File
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={classNames(
        "adhoc-upload-documents-container-form-inputs-table-row",
        {
          "thick-common-border-bottom": shouldThickTheBorder,
          "common-border-bottom": !shouldThickTheBorder,
        }
      )}
    >
      <div className="adhoc-upload-documents-container-form-inputs-table-row-header">
        <div className="header-title">{document.documentName}</div>
        {(document.status?.toLowerCase() === "pending" ||
          uploadedFiles[document.id]) &&
          uploadedFiles[document.id] && (
            <div>
              <span className="pl-2" style={{ fontSize: "14px" }}>
                {`${uploadedFiles[document.id].name}`}
              </span>
            </div>
          )}
        {document.status?.toLowerCase() === "submitted" &&
          !uploadedFiles[document.id] && (
            <div>
              <img src={tickCheck} alt="tick-check" />
              <span
                className="pl-2"
                style={{ fontSize: "14px", color: "#65A30D" }}
              >
                {`Submitted on ${formatISODateToLongDate(
                  document.submittedAt
                )}`}
              </span>
            </div>
          )}
      </div>
      <div className="adhoc-upload-documents-container-form-inputs-table-row-action">
        {renderActions()}
      </div>
    </div>
  );
}

// Helper component for uploading file
function UploadFileLink({
  label,
  documentId,
  onFileUpload,
  isRequestPending = true,
}) {
  const fileInputRef = useRef(null);

  const handleFileInputChange = () => {
    const selectedFile = fileInputRef.current.files[0];

    // Check if a file is selected
    if (selectedFile) {
      // Get the file extension
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

      // Check if the file extension is 'pdf'
      if (fileExtension === "pdf") {
        // Proceed with file upload
        onFileUpload(documentId, selectedFile);
      } else {
        // Display error message or handle invalid file type
        notify(
          "Error",
          "Failed to upload the file. Please make sure you have selected a PDF file.",
          "error"
        );
      }
    }
  };

  const handleButtonClick = () => {
    if (!isRequestPending) return;
    fileInputRef.current.click();
  };

  return (
    <div
      onClick={handleButtonClick}
      style={{
        opacity: isRequestPending ? 1 : 0.5,
        cursor: isRequestPending ? "pointer" : "not-allowed",
      }}
    >
      {label}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
    </div>
  );
}

// Helper functions
function transformRequestData(data) {
  // Check if data is provided and has the expected structure
  if (!Array.isArray(data) || data.length === 0) {
    console.error("Invalid or empty data provided.");
    return { userDetails: {}, requestDetails: {}, documents: [] }; // Return empty objects and array
  }

  // Extract user details
  const userDetails = {
    email: data[0]?.documentRequestUser?.email || "", // Use optional chaining to handle missing values
  };

  // Extract request details
  const requestDetails = {
    id: data[0]?.documentRequestUser?.documentRequest?.id || "",
    title: data[0]?.documentRequestUser?.documentRequest?.title || "",
    dueDate: data[0]?.documentRequestUser?.documentRequest?.dueDate || "",
    emailDescription:
      data[0]?.documentRequestUser?.documentRequest?.emailDescription || "",
    documentsCount:
      data[0]?.documentRequestUser?.documentRequest?.documentsCount || 0,
    status: data[0]?.documentRequestUser?.documentRequest?.status || "",
    userOwnRequestStatus: data[0]?.documentRequestUser?.submissionStatus || "",
  };

  // Extract documents
  const documents = sort(
    data.map((submission) => ({
      id: submission.id || "",
      status: submission.submissionStatus || "",
      submissionFile: submission.submissionFile || "",
      documentName: submission.documentRequestDocument?.documentName || "",
      documentId: submission.documentRequestDocument?.id || "",
      submittedAt: submission.submittedAt || "",
      requestedDocument:
        typeof submission.documentRequestDocument?.document === "string"
          ? null
          : -1,
    }))
  ).asc((u) => u?.documentName?.toLowerCase());

  return { userDetails, requestDetails, documents };
}

function createStateVariables(state) {
  const documentSubmissionsInfo = [];
  const files = [];

  for (const submissionId in state) {
    if (state.hasOwnProperty(submissionId)) {
      const submission = state[submissionId];
      documentSubmissionsInfo.push({
        submissionId: submissionId,
        submissionFileName: submission.name,
      });
      files.push(submission.file);
    }
  }

  return { documentSubmissionsInfo, files };
}

function getStatusValue(
  isRequestExpired,
  isRequestPending,
  isAllDocsSubmitted,
  isCancelledRequest
) {
  if (isRequestExpired) {
    return "expired"; // If request is expired
  } else if (isAllDocsSubmitted) {
    return "done"; // If all documents are submitted
  } else if (isCancelledRequest) {
    return "cancelled"; // If request is not pending
  } else {
    return "pending"; // Default: if request is pending and not expired
  }
}
